/* eslint-disable */
import React, { useState, useEffect, useMemo } from 'react';
import { FormHeader, FormBody, FormFooter, FormSection } from 'ui/Form';
import InputWrapper from 'components/InputWrapper';
import Button, { GhostButton } from 'ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { connect } from 'react-redux';
import { getGroups } from 'reducers/groups/groupsActions';
import { createStructuredSelector } from 'reselect';
import { groupsSelector } from 'reducers/groups/groupsSelectors';
import Select from 'ui/Select';
import PropTypes from 'prop-types';
import Dashboard, { DashboardHeader } from 'components/Dashboard';
import { activeProjectUsersSelector } from 'reducers/projects/activeProjectSelectors';
import ImmutableConverter from 'HOCs/ImmutableConverter';
import { UserChip } from 'components/Chip/Chip';
import { HeightCappedList } from 'css/helperStyles';
import { updateGroupUsers } from 'services/api/groupsApi';
import LoadingIndicator from 'ui/LoadingIndicator';
import { getUsers } from 'actions/users';
import { alphaSort } from 'utils/sortFunctions';
import { fetchGroups } from 'services/api/groupsApi';
import styled from 'styled-components/macro';
import { colors } from '../../../css/constants';
import Form from 'react-bootstrap/Form';
import FormTooltip from '../../../components/FormTooltip/FormTooltip';
import { GROUP_COORDINATOR_DESCRIPTION } from '../../../constants/messages';

const ListHeaders = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Header = styled.h6`
  color: ${colors.sohoGray};
  display: flex;
  gap: 0.25em;
  align-items: center;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1em;
`;

const UserChipFull = styled(UserChip)`
  width: 100%;
`;

const Tooltip = styled(FormTooltip)`
  margin: 0;
`;

export const AddUserToGroupForm = ({
  projectId,
  selectedUserIds,
  users,
  groups,
  dispatch,
  hideModal,
  onSubmit
}) => {
  const [groupInput, setGroupInput] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userRoles, setUserRoles] = useState({});
  const selectedUserIdsSet = new Set(selectedUserIds);
  const selectedUsers = useMemo(
    () =>
      users
        .filter(({ id }) => selectedUserIdsSet.has(id))
        .sort(alphaSort({ accessor: (u) => u.last_name })),
    [users, selectedUserIds]
  );
  const isValid = groupInput !== null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    const groupUsers = [...selectedUserIdsSet].map((user_id) => ({
      user_id,
      roles: userRoles[user_id] ? ['group_coordinator'] : []
    }));
    await updateGroupUsers(projectId, groupInput.id, groupUsers);
    dispatch(getUsers(projectId));
    setIsSubmitting(false);
    hideModal();
    onSubmit();
  };

  const handleGroupChange = (_name, value) => {
    const roles = selectedUsers
      .map(({ id, group_roles }) => [id, group_roles])
      .map(([id, groupRoles]) => [
        id,
        groupRoles.find(({ group_id }) => group_id === value.id)?.roles
      ])
      .map(([id, roles]) => [id, roles?.includes('group_coordinator')]);

    setGroupInput(value);
    setUserRoles(Object.fromEntries(roles));
  };

  const handleRoleChange = ({ target }) => {
    const { id, checked } = target;
    setUserRoles({ ...userRoles, [id]: checked });
  };

  useEffect(() => {
    fetchGroups(projectId).then((response) => dispatch(getGroups(response)));
  }, []);

  const groupOptions = groups.map((group) => ({
    label: group.name,
    value: group
  }));

  return (
    <Dashboard>
      <DashboardHeader>
        <FormHeader data-testid="formHeader">
          <FontAwesomeIcon icon={regular('user')} /> Add Users to a Group
        </FormHeader>
      </DashboardHeader>
      <form onSubmit={handleSubmit}>
        <FormBody>
          <FormSection>
            <InputWrapper
              noPadding
              name="group"
              label="Group"
              version="v2"
              fullWidth
            >
              <Select
                id="group"
                name="group"
                options={groupOptions}
                onChange={handleGroupChange}
                value={groupInput?.value}
              />
            </InputWrapper>
          </FormSection>
          <FormSection gridTemplateCols="repeat(1, 1fr)">
            <p>The following Users will be added to the selected group:</p>
            <ListHeaders>
              <Header>User</Header>
              <Header>
                <FontAwesomeIcon icon={regular('user-cog')} />
                Group Coordinator
                <Tooltip
                  message={GROUP_COORDINATOR_DESCRIPTION}
                  place="bottom"
                  type="light"
                />
              </Header>
            </ListHeaders>
            <HeightCappedList>
              {selectedUsers.map(({ id, first_name, last_name, email }) => (
                <ListItem key={id}>
                  <UserChipFull>
                    {first_name} {last_name} - {email}
                  </UserChipFull>
                  <Form.Check
                    type="switch"
                    id={id}
                    checked={userRoles[id]}
                    disabled={!isValid}
                    onChange={handleRoleChange}
                    data-testid={`role-switch-${id}`}
                  />
                </ListItem>
              ))}
            </HeightCappedList>
          </FormSection>
        </FormBody>
        <FormFooter>
          <FormSection noMarginBottom>
            <GhostButton data-testid="addGroup-cancel-btn" onClick={hideModal}>
              Cancel
            </GhostButton>
            <Button
              data-testid="addGroup-confirm-btn"
              type="submit"
              disabled={!isValid}
            >
              Confirm
            </Button>
          </FormSection>
        </FormFooter>
      </form>
      {isSubmitting && <LoadingIndicator cover />}
    </Dashboard>
  );
};

AddUserToGroupForm.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  dispatch: PropTypes.func,
  groups: PropTypes.arrayOf(PropTypes.object),
  hideModal: PropTypes.func
};

AddUserToGroupForm.defaultProps = {
  dispatch: () => {},
  hideModal: () => {}
};

const mapStateToProps = createStructuredSelector({
  groups: groupsSelector,
  users: activeProjectUsersSelector
});
const mapDispatchToProps = (dispatch) => ({ dispatch });
const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default withConnect(ImmutableConverter(AddUserToGroupForm));
